/* istanbul ignore file */
import axios from 'axios';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAccessToken, getBaseURL } from '../baseFetch';
import { formatBuoyReportTableData } from '../../utils/formatBuoyReportTableData';
import { getMeterParamsForRequest } from '../../utils/meteringHelper';

/**
 * @typedef {import('../../propTypes/buoys').Station} Station
 * @typedef {import('../../propTypes/buoys').NearbyStation} NearbyStation
 * @typedef {import('../../propTypes/map/location').BoundingBox} BoundingBox
 * @typedef {import('../../propTypes/units').Units} Units
 *
 * @typedef {object} NearbyBuoysResponse
 * @property {object} associated
 * @property {Units} associated.units
 * @property {NearbyStation[]} data
 *
 * @typedef {object} BoundingBoxResponse
 * @property {object} associated
 * @property {Units} associated.units
 * @property {NearbyStation[]} data
 *
 * @typedef {object} BuoyDetailsResponse
 * @property {object} associated
 * @property {string} associated.abbrTimezone
 * @property {Station['station']} data
 *
 * @typedef {object} BuoyReportResponse
 * @property {object} associated
 * @property {string} associated.abbrTimezone
 * @property {Units} associated.units
 * @property {Array<Station['data']>} data
 */

// We don't need to support server-side render for buoys so we can
// just retrieve the acccess token from cookies at the top level

const ACCESS_TOKEN = getAccessToken();
const BASE_URL = getBaseURL();
const TWENTY_MINUTES_IN_SECONDS = 1200;

/**
 * @param {Station['station']['id']} id
 */
export const getBuoyDetails = async (id) => {
  /** @type {import('axios').AxiosResponse<BuoyDetailsResponse>} */
  const response = await axios.get(`${BASE_URL}/kbyg/buoys/details/${id}`, {
    params: { accesstoken: ACCESS_TOKEN },
  });

  return response.data;
};

/**
 * Using a radius to retrieve all current data for all buoys within the radius of the lat/lon
 * @param {object} args
 * @param {number} args.latitude
 * @param {number} args.longitude
 * @returns {import('axios').AxiosResponse<NearbyBuoysResponse>['data']}
 */
export const getNearbyBuoys = async ({ latitude, longitude }) => {
  const response = await axios.get(`${BASE_URL}/kbyg/buoys/nearby`, {
    params: { latitude, longitude, accesstoken: ACCESS_TOKEN },
  });

  return response.data;
};

/**
 * Using a bounding box to retrieve all current data for all buoys within that bounding box
 * @param {BoundingBox} boundingBox
 */
export const getBuoysInBoundingBox = async (boundingBox) => {
  /** @type {import('axios').AxiosResponse<BoundingBoxResponse>} */
  const response = await axios.get(`${BASE_URL}/kbyg/buoys/bounds`, {
    params: { ...boundingBox, accesstoken: ACCESS_TOKEN },
  });

  return response.data;
};

/**
 * Use the `id` to retrieve all current and historical data for a single buoy station
 * @param {object} args
 * @param {Station['station']['id']} args.id
 * @param {import('../../propTypes/meter').MeterProp} args.meter
 */
export const getBuoyReport = async ({ id, meter }) => {
  const { meterHeaders, meterRemaining } = getMeterParamsForRequest(meter);

  /** @type {import('axios').AxiosResponse<BuoyReportResponse>} */
  const response = await axios.get(`${BASE_URL}/kbyg/buoys/report/${id}?days=3`, {
    params: { accesstoken: ACCESS_TOKEN, meterRemaining },
    headers: { ...meterHeaders },
  });

  return {
    data: formatBuoyReportTableData(response.data),
    associated: response.data.associated,
  };
};

const queryWrapper =
  (query) =>
  async (...args) => {
    try {
      const response = await query(...args);
      return { data: response };
    } catch (error) {
      return { error: { message: error.message, status: error.response?.status } };
    }
  };

/**
 * @template D
 * @typedef {object} WrappedQuery
 * @property {D} data
 * @property {{ message: string, status: number }} error
 */

export const buoysAPI = createApi({
  reducerPath: 'buoysAPI',
  keepUnusedDataFor: TWENTY_MINUTES_IN_SECONDS,
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    getBuoyDetails: builder.query({
      /** @type {() => Promise<WrappedQuery<BuoyDetailsResponse>>} */
      queryFn: queryWrapper(getBuoyDetails),
    }),
    getNearbyBuoys: builder.query({
      /** @type {() => Promise<WrappedQuery<NearbyBuoysResponse>>} */
      queryFn: queryWrapper(getNearbyBuoys),
    }),
    getBuoysInBoundingBox: builder.query({
      /** @type {() => Promise<WrappedQuery<BoundingBoxResponse>>} */
      queryFn: queryWrapper(getBuoysInBoundingBox),
    }),
    getBuoyReport: builder.query({
      /** @type {() => Promise<WrappedQuery<ReturnType<typeof getBuoyReport>>>} */
      queryFn: queryWrapper(getBuoyReport),
    }),
  }),
});
